import { IAction } from 'types';
import {
    call,
    select
} from 'typed-redux-saga';
import { IGeometry } from 'types/omjs/project';
import {
    getOmjsContainer,
    getOmjsViewInstance,
    getOmjsViewLayer, getOperationData
} from 'selectors';
import { Extent } from 'ol/extent';
import { MultiPolygon, Polygon } from 'ol/geom';
import { pkkSearchByCoordinate } from 'api/pkk';
import { EBalloonTypes } from 'scenes/map/map';
import {
    Service,
    bindActionsTyped,
    every,
    leading
} from '../service-utils';

import { operation, OperationsService } from '../operations/operations-service';
import { IState } from '../root-reducer';
import { getMaxPolygonFromMultiPolygon } from '../../utils';

export interface ILayerAndObjectPair {
    layer: string | number;
    objectId: number
}

export interface IConnection {
    distance: number,
    coordinateFrom: [number, number],
    coordinateTo: [number, number],
    power: number
}

export interface IMapStateOperationData {
    sysGeometry: IGeometry;
    loader: [ number, number ];
    baseMapId: number;
    cadastreVisible: boolean;
    cadastreHighlightGeometry: IGeometry | null;
    fitTarget: number[] | ILayerAndObjectPair;
    fitExtent: Extent;
    yaAddressMarker: [number, number];
    activeRasterLayers: number[];
    activeGpRasterLayers: number[];
    activeVectorLayers: number[];
    balloonType: string;
    balloonCoords: [number, number];
    connections: {[key: string]: {objects: IConnection[], enabled: boolean}}
    connectionsRevision: number,
}

export const defaultMapState: IMapStateOperationData = {
    sysGeometry: null,
    loader: null,
    baseMapId: null,
    cadastreVisible: false,
    cadastreHighlightGeometry: null,
    fitTarget: null,
    fitExtent: null,
    yaAddressMarker: null,
    activeRasterLayers: [],
    activeGpRasterLayers: [],
    activeVectorLayers: [],
    balloonType: null,
    balloonCoords: null,
    connections: {
        powers: { objects: [], enabled: false },
        gas: { objects: [], enabled: false },
        waterSupply: { objects: [], enabled: false },
        sewerage: { objects: [], enabled: false }
    },
    connectionsRevision: 0
};

export class MapService extends Service {
    static serviceName = 'MapService';
    static generatedStopActionTypes: string[] = [];
    static generatedWatchers: Function[] = [];

    @every
    @operation('MAP_STATE')
    static* showLoader(action: IAction<[ number, number ]>) {
        yield* call(OperationsService.updateData, 'MAP_STATE', { loader: action.payload });
    }

    @every
    @operation('MAP_STATE')
    static* setObjectHighlight(action: IAction<IGeometry>) {
        yield* call(OperationsService.updateData, 'MAP_STATE', { sysGeometry: action.payload });
    }

    @every
    @operation('MAP_STATE')
    static* hideLoader() {
        yield* call(OperationsService.updateData, 'MAP_STATE', { loader: null });
    }

    @every
    @operation('MAP_STATE')
    static* showBalloon(action: IAction<{ type: string, coords: [number, number] }>) {
        yield* call(OperationsService.updateData, 'MAP_STATE', {
            balloonType: action.payload.type,
            balloonCoords: action.payload.coords
        });
    }

    @every
    @operation('MAP_STATE')
    static* hideBalloon() {
        yield* call(OperationsService.updateData, 'MAP_STATE', {
            balloonType: null,
            balloonCoords: null
        });
    }

    @every
    static* highlightObject(action: IAction<{ layerCode: string, objectId: number }>) {
        const { layerCode, objectId } = action.payload;
        const layer = yield* select(getOmjsViewLayer, layerCode);

        if (layer.options['layerType'] !== 'oms_vector_category') {
            const source = layer.getSource();
            if (source instanceof omjs.ol.source.Cluster) {
                for (let feature of source.getFeatures()) {
                    for (let f of feature.get('features')) {
                        if (objectId === f.get('obj').getId()) {
                            yield* call(MapService.showLoader, {
                                type: 'showLoader',
                                payload: feature.getGeometry().getCoordinates()
                            });
                        }
                    }
                }
            } else if (source instanceof omjs.ol.source.Vector) {
                for (let feature of source.getFeatures()) {
                    if (objectId === feature.get('obj').getId()) {
                        yield* call(MapService.showLoader, {
                            type: 'showLoader',
                            payload: feature.getGeometry().getCoordinates()
                        });
                    }
                }
            }
        } else {
            const pLayer = layer.getProjectLayers().at(0);
            const object = pLayer.objects.getArray().find(obj => obj.getId() === objectId);
            yield* call(MapService.showLoader, {
                type: 'showLoader',
                payload: object.feature.getGeometry().getCoordinates()
            });
        }
    }

    @every
    @operation('MAP_STATE')
    static* switchBaseLayer(action: IAction<number>) {
        const view = yield* select(getOmjsViewInstance);
        view.switchBaseLayer(action.payload);
        yield* call(OperationsService.updateData, 'MAP_STATE', { baseMapId: action.payload });
    }

    @every
    @operation('MAP_STATE')
    static* showCadastreLayer(action: IAction<boolean>) {
        yield* call(OperationsService.updateData, 'MAP_STATE', {
            cadastreVisible: action.payload
        });
    }

    @every
    @operation('MAP_STATE')
    static* highlightCadastreGeometry(action: IAction<IGeometry | null>) {
        yield* call(OperationsService.updateData, 'MAP_STATE', {
            cadastreHighlightGeometry: action.payload
        });
    }

    @every
    static* fitToTarget(action: IAction<number[] | ILayerAndObjectPair>) {
        yield* call(OperationsService.updateData, 'MAP_STATE', { fitTarget: action.payload });
    }

    @every
    static* fitToExtent(action: IAction<Extent>) {
        yield* call(OperationsService.updateData, 'MAP_STATE', { fitExtent: action.payload });
    }

    @every
    static* placeYaAddressMarker(action: IAction<[ number, number ]>) {
        yield* call(OperationsService.updateData, 'MAP_STATE', {
            yaAddressMarker: action.payload
        });
    }

    static* calcConnections(layerCode, fieldCode, filterType, coordinate) {
        const view = yield* select(getOmjsViewInstance);
        const filters = yield* select(getOperationData, 'FILTERS');
        const container = yield* select(getOmjsContainer, layerCode, true);
        const filter = filters['plots'].find(f => f.type === filterType);
        let objects = container.objects.getArray();

        let condition;
        switch (filter.type) {
            case 'powers':
            case 'gas': {
                condition = filter.value[2]
                    ? { power: filter.value[0], dist: filter.value[1] }
                    : { power: undefined, dist: (filterType === 'powers') ? 6 : 10 };
                break;
            }
            case 'water_supply':
            case 'sewerage': {
                condition = {
                    power: undefined,
                    dist: 1
                };
                break;
            }
            default: {
                condition = {
                    power: filter.value[2] ? filter.value[0] : undefined,
                    dist: filter.value[2] ? filter.value[1] : 10
                };
            }
        }
        const sourceProj = view.getMap().getView().getProjection();
        const connections = objects
            .reduce((acc, obj) => {
                const geom = obj.getGeometry();
                const isPower = fieldCode === null
                    ? false
                    : (obj.get(fieldCode) === null) || parseFloat(obj.get(fieldCode)) < condition.power;
                if (isPower || !geom) {
                    return acc;
                }
                const c1 = omjs.ol.proj.transform(coordinate, sourceProj, 'EPSG:4326');
                const c2 = omjs.ol.proj.transform(geom.coordinates, sourceProj, 'EPSG:4326');
                const calculatedDistance = omjs.ol.sphere.getDistance(c1, c2);
                if (calculatedDistance <= condition.dist * 1000) {
                    acc.push({
                        distance: calculatedDistance,
                        power: fieldCode === null ? null : parseFloat(obj.get(fieldCode)),
                        coordinateTo: geom.coordinates,
                        coordinateFrom: coordinate
                    });
                }
                return acc;
            }, []);
        return {
            connections,
            selectedContainer: container.get('selected')
        };
    }

    @every
    static* getConnections(action: IAction<[number, number]>) {
        const powersCode = 'ia34';
        const powerField = 'power';
        const gasCode = 'ii_obj_gazosnabzh_new';
        const gasField = 'throughput_gas';
        const waterSupplyCode = 'water_supply';
        const sewerageCode = 'sewerage';

        const mapState = yield* select(getOperationData, 'MAP_STATE');
        if (!action.payload[0] && !action.payload[1]) {
            let ifMustBeReset = false;
            for (const key in mapState.connections) {
                if (mapState.connections[key].objects.length > 0) ifMustBeReset = true;
            }
            if (ifMustBeReset) {
                yield* call(OperationsService.updateData, 'MAP_STATE', {
                    connections: {
                        powers: { objects: [], enabled: mapState.connections.powers.enabled },
                        gas: { objects: [], enabled: mapState.connections.gas.enabled },
                        waterSupply: { objects: [], enabled: mapState.connections.waterSupply.enabled },
                        sewerage: { objects: [], enabled: mapState.connections.sewerage.enabled }
                    },
                    connectionsRevision: mapState.connectionsRevision + 1
                });
            }
            return;
        }

        const { connections: powers, selectedContainer: selectedPowers } = yield call(MapService.calcConnections,
            powersCode, powerField, 'powers', action.payload);
        const { connections: gas, selectedContainer: selectedGas } = yield call(MapService.calcConnections,
            gasCode, gasField, 'gas', action.payload);
        const { connections: waterSupply, selectedContainer: selWaterSupply } = yield call(MapService.calcConnections,
            waterSupplyCode, null, 'water_supply', action.payload);
        const { connections: sewerage, selectedContainer: selSewerage } = yield call(MapService.calcConnections,
            sewerageCode, null, 'sewerage', action.payload);
        yield* call(OperationsService.updateData, 'MAP_STATE', {
            connections: {
                powers: { objects: powers, enabled: selectedPowers },
                gas: { objects: gas, enabled: selectedGas },
                waterSupply: { objects: waterSupply, enabled: selWaterSupply },
                sewerage: { objects: sewerage, enabled: selSewerage }
            },
            connectionsRevision: mapState.connectionsRevision + 1
        });
    }

    static* updatePowersEnabled(enabled: {
        powers?: boolean,
        gas?: boolean,
        waterSupply?: boolean,
        sewerage?: boolean
    }) {
        const oldConnections = yield* select((state: IState) => getOperationData(state, 'MAP_STATE').connections);
        const connections = { ...oldConnections };
        for (let key in connections) {
            if (key in enabled) {
                connections[key] = { ...connections[key], enabled: enabled[key] };
            }
        }
        yield* call(OperationsService.updateData, 'MAP_STATE', { connections });
    }

    @every
    static* getConnectionsByObject(action: IAction<{ objectId: number, layerCode: string }>) {
        const { objectId, layerCode } = action.payload;
        const container = yield* select(getOmjsContainer, layerCode, true);
        const object = container.objects.getArray().find(obj => obj.getId() === objectId);

        let coords = object.getGeometry().coordinates;
        const { type } = object.getGeometry();
        const format = new omjs.ol.format.GeoJSON();
        if (type === 'Polygon') {
            const geom = format.readGeometry(object.getGeometry()) as Polygon;
            coords = geom.getInteriorPoint().getCoordinates();
        } else if (type === 'MultiPolygon') {
            const largestPolygon = getMaxPolygonFromMultiPolygon(format.readGeometry(object.getGeometry()));
            coords = largestPolygon.getInteriorPoint().getCoordinates();
        }
        yield call(MapService.getConnections, { type: 'getConnections', payload: coords });
    }

    // @every
    // static* setActiveMapLayers(action: IAction<[ number[], number[] ]>) {
    //     const [ raster, vector ] = action.payload;
    //     yield* call(OperationsService.updateData, 'MAP_STATE', {
    //         activeRasterLayers: raster,
    //         activeVectorLayers: vector
    //     });
    // }

    // @every
    // static* clearYaAddressMarket() {
    //     yield* call(OperationsService.updateData, 'MAP_STATE', {
    //         yaAddressMarker: null
    //     });
    // }

    @leading
    static* printMap() {
        let view = yield* select(getOmjsViewInstance);
        let baseLayer = view.getBaseLayer();

        omjs.$('body').addClass('print');

        let mapCont = omjs.$('.omjs-map-container');
        let left = mapCont.css('left');

        // // Устанавливаем размер области карты для печати
        mapCont.css({
            'width': 900,
            'height': 900 / 1.42,
            'left': 0
        });

        // // Обновляем размеры карты
        view.getMap().updateSize();

        setTimeout(() => {
            if (baseLayer._yaMap) {
                baseLayer._yaMap.container.fitToViewport();
            }

            /*
                На ирисе (ленинградская область) карт гугл не было, если на вашем проекте они есть, то
                эти строчки надо раcкомментировать. Попробуйте разобраться в этом сами.
            */
            // if (baseLayer._googMap) {
            //     let center = ol.proj.transform(app.view.getMap().getView().getCenter(), 'EPSG:3857', 'EPSG:4326');
            //     let goCenter = new google.maps.LatLng(center[1], center[0]);
            //     google.maps.event.trigger(baseLayer._googMap, 'resize');
            //     baseLayer._googMap.setCenter(goCenter);
            // }

            window.print();
            setTimeout(() => {
                mapCont.css({
                    'width': 'auto',
                    'height': 'auto',
                    'left': left
                });

                view.getMap().updateSize();

                if (baseLayer._yaMap) {
                    baseLayer._yaMap.container.fitToViewport();
                }

                /*
                    На ирисе (ленинградская область) карт гугл не было, если на вашем проекте они есть, то
                    эти строчки надо раcкомментировать. Попробуйте разобраться в этом сами.
                */
                /* eslint-disable max-len */
                // if (baseLayer._googMap) {
                //     let center = ol.proj.transform(app.view.getMap().getView().getCenter(), 'EPSG:3857', 'EPSG:4326');
                //     let goCenter = new google.maps.LatLng(center[1], center[0]);
                //     google.maps.event.trigger(baseLayer._googMap, 'resize');
                //     baseLayer._googMap.setCenter(goCenter);
                // }
                /* eslint-enable max-len */


                omjs.$('body').removeClass('print');
            }, 200);
        }, 20);
    }

    @every
    static* setActiveLayers(raster, vector, gpRaster) {
        yield* call(OperationsService.updateData, 'MAP_STATE', {
            activeRasterLayers: raster,
            activeVectorLayers: vector,
            activeGpRasterLayers: gpRaster
        });
    }

    @leading
    @operation('SHOW_PKK_BALLOON')
    static* showPkkBalloon(action: IAction<[number, number]>) {
        const [ lon, lat ] = action.payload;
        const view = yield select(getOmjsViewInstance);
        const zoom = view.getMap().getView().getZoom();

        const { data } = yield* call(pkkSearchByCoordinate, { lon, lat, zoom });

        if (data?.features.length > 0) {
            yield call(MapService.showBalloon, {
                type: 'showBalloon',
                payload: { type: EBalloonTypes.PKKObject, coords: action.payload }
            });
            yield* call(MapService.hideLoader);
        }

        return { features: data?.features || [] };
    }
}


export const mapService: MapService = new MapService();
export const actions = bindActionsTyped(MapService, mapService);
